// Locale configuration and the pure resolution rule shared by the server
// (get-locale.ts) and the unit tests. No Next.js imports here on purpose.

export const LOCALES = ["en", "pl"] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = "en";

/** Cookie written by the language switcher (setLocale server action). */
export const LOCALE_COOKIE = "NEXT_LOCALE";
/** One year, in seconds. */
export const LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/** Open Graph locale tags per UI locale. */
export const OG_LOCALE: Record<Locale, string> = { en: "en_US", pl: "pl_PL" };

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (LOCALES as readonly string[]).includes(value);
}

/**
 * Best supported locale from an Accept-Language header, honouring q-values.
 * `pl-PL,pl;q=0.9,en;q=0.8` -> "pl"; unsupported or missing -> null.
 */
export function localeFromAcceptLanguage(header: string | null | undefined): Locale | null {
  if (!header) return null;
  const ranked = header
    .split(",")
    .map((part, index) => {
      const [tag = "", ...params] = part.trim().split(";");
      const q = params
        .map((p) => p.trim())
        .find((p) => p.startsWith("q="));
      const quality = q ? Number.parseFloat(q.slice(2)) : 1;
      return {
        base: tag.trim().toLowerCase().split("-")[0] ?? "",
        quality: Number.isFinite(quality) ? quality : 0,
        index,
      };
    })
    .filter((entry) => entry.base && entry.quality > 0)
    // Higher quality first; equal quality keeps header order.
    .sort((a, b) => b.quality - a.quality || a.index - b.index);
  for (const entry of ranked) {
    if (isLocale(entry.base)) return entry.base;
  }
  return null;
}

/**
 * Locale for a request: the visitor's explicit choice (NEXT_LOCALE cookie,
 * set by the language switcher), otherwise English. The browser language is
 * deliberately not used: the site opens in English for everyone until the
 * visitor picks Polish.
 */
export function resolveLocale(cookieValue: string | null | undefined): Locale {
  return isLocale(cookieValue) ? cookieValue : DEFAULT_LOCALE;
}
